import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { exportGuideToPDF } from '@/lib/pdfExport';
import { AudioSettingsPanel } from '@/components/AudioSettingsPanel';
import { 
  ArrowRight, ArrowLeft, Building2, CalendarDays, 
  Bell, CheckCircle, Clock, XCircle, Download, BookOpen,
  Smartphone, Loader2, Volume2, Download as DownloadIcon
} from 'lucide-react';
import { isPWAInstalled, getInstallInstructions, isIOS, isAndroid } from '@/lib/pwa';
import BrandLogo from '../../components/BrandLogo';

const steps = [
  {
    id: 1,
    title: "Welcome to ExpressSub!",
    subtitle: "Find substitute teaching positions easily",
    icon: Clock,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          ExpressSub connects you with schools looking for substitute teachers. You'll be able to:
        </p>
        <ul className="space-y-3">
          <li className="flex items-center gap-3">
            <div className="h-8 w-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <Building2 className="h-4 w-4 text-emerald-600" />
            </div>
            <span>Connect with multiple schools</span>
          </li>
          <li className="flex items-center gap-3">
            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Bell className="h-4 w-4 text-blue-600" />
            </div>
            <span>Get instant notifications for new positions</span>
          </li>
          <li className="flex items-center gap-3">
            <div className="h-8 w-8 bg-amber-100 rounded-full flex items-center justify-center">
              <CalendarDays className="h-4 w-4 text-amber-600" />
            </div>
            <span>Accept positions with one tap</span>
          </li>
        </ul>
      </div>
    )
  },
  {
    id: 2,
    title: "Install ExpressSub Web App",
    subtitle: "Get the best experience on your desktop",
    icon: Smartphone,
    content: (() => {
      const instructions = getInstallInstructions();
      const installed = isPWAInstalled();
      
      return (
        <div className="space-y-4">
          {installed ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
              <CheckCircle className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
              <p className="font-medium text-emerald-800">ExpressSub is installed!</p>
              <p className="text-sm text-emerald-700">You're using the app version</p>
            </div>
          ) : (
            <>
              <p className="text-slate-600">
                Install ExpressSub on your {instructions.platform} for instant notifications and quick access:
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <DownloadIcon className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-800">How to Install:</span>
                </div>
                <ol className="space-y-2 text-sm text-blue-700">
                  {instructions.steps.map((step, index) => (
                    <li key={step} className="flex items-start gap-2">
                      <span className="font-bold">{index + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <p className="text-sm text-slate-500 italic">
                You can skip this step and install later from your browser's menu.
              </p>
            </>
          )}
          <p className="text-sm text-slate-600 border-t border-slate-200 pt-3">
            For the phone app, visit the App Store or Google Play.
          </p>
        </div>
      );
    })()
  },
  {
    id: 3,
    title: "Set Your Notification Sound",
    subtitle: "Choose a unique alert you'll recognize",
    icon: Volume2,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          Position offers move fast! Set a <strong>distinctive notification sound</strong> so you'll instantly know when a job is available:
        </p>
        <AudioSettingsPanel compact={true} />
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            <strong>💡 Pro Tip:</strong> Choose a sound that stands out from your other apps. 
            Turn up your volume and make sure your phone isn't on silent mode!
          </p>
        </div>
      </div>
    )
  },
  {
    id: 4,
    title: "Connecting to Schools",
    subtitle: "Join schools using their unique code",
    icon: Building2,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          To start receiving positions from a school:
        </p>
        <ol className="space-y-4">
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
            <span>Ask the school administrator for their <strong>ExpressSub school code</strong></span>
          </li>
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
            <span>Click <strong>"Connect to School"</strong> on your dashboard</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
            <span>Enter the code and submit your request</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
            <span>Wait for the school to approve your request</span>
          </li>
        </ol>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Connect to multiple schools to maximize your opportunities!
          </p>
        </div>
      </div>
    )
  },
  {
    id: 5,
    title: "Responding to Positions",
    subtitle: "Accept or decline positions quickly",
    icon: CalendarDays,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          When a position is posted, you'll see it on your dashboard:
        </p>
        <div className="space-y-3">
          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              <span className="font-medium text-emerald-800">Accept</span>
            </div>
            <p className="text-sm text-emerald-700">Click to take the position. First to accept gets it!</p>
          </div>
          <div className="bg-rose-50 rounded-lg p-4 border border-rose-200">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="h-5 w-5 text-rose-600" />
              <span className="font-medium text-rose-800">Decline</span>
            </div>
            <p className="text-sm text-rose-700">Can't make it? Decline so the school knows to find someone else.</p>
          </div>
        </div>
        <p className="text-sm text-slate-500">
          <strong>Note:</strong> Some positions have multiple dates. You must accept all dates or decline entirely.
        </p>
      </div>
    )
  },
  {
    id: 6,
    title: "Enable Browser Notifications",
    subtitle: "Never miss an opportunity",
    icon: Bell,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          Position offers are time-sensitive. Enable notifications to respond first!
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <Bell className="h-10 w-10 text-amber-600 mx-auto mb-3" />
          <p className="font-medium text-amber-800 mb-2">Allow browser notifications</p>
          <p className="text-sm text-amber-700">When prompted, click "Allow" to enable push notifications</p>
        </div>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <span>Get instant alerts even when the app is in the background</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <span>Your unique ExpressSub sound will play (set in step 3)</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <span>Be the first to accept positions!</span>
          </li>
        </ul>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>Important:</strong> Make sure your device isn't on silent mode and volume is turned up!
          </p>
        </div>
      </div>
    )
  },
  {
    id: 7,
    title: "Managing Your Day",
    subtitle: "Control when you receive offers",
    icon: XCircle,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          Not available today? Use "Decline All for Today" to take a break:
        </p>
        <div className="bg-slate-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="px-3 py-1.5 bg-rose-100 text-rose-700 rounded-full text-sm font-medium">
              Decline All for Today
            </div>
          </div>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5" />
              <span>You won't receive new position offers for the rest of the day</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5" />
              <span>All your connected schools will be notified</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5" />
              <span>You can undo this anytime to become available again</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5" />
              <span>Resets automatically each day</span>
            </li>
          </ul>
        </div>
      </div>
    )
  },
  {
    id: 8,
    title: "You're All Set!",
    subtitle: "Start finding positions",
    icon: CheckCircle,
    content: (
      <div className="space-y-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
          <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-emerald-800 mb-2">Ready to Go!</h3>
          <p className="text-emerald-700">You're all set to start finding positions</p>
        </div>
        <div className="space-y-2">
          <p className="font-medium text-slate-700">Quick Start Checklist:</p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              Install ExpressSub as an app for the best experience
            </li>
            <li className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              Set your unique notification sound
            </li>
            <li className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              Get school codes from administrators
            </li>
            <li className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              Enable notifications &amp; turn up your volume
            </li>
            <li className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              Respond quickly when positions are posted!
            </li>
          </ul>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Volume2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            <strong>Remember:</strong> Turn off silent mode and keep your volume up to hear your unique ExpressSub alert!
          </p>
        </div>
      </div>
    )
  }
];

export default function SubOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark onboarding as complete
      localStorage.setItem('sub_onboarding_complete', 'true');
      navigate('/toc');
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('sub_onboarding_complete', 'true');
    navigate('/toc');
  };

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    try {
      const success = await exportGuideToPDF('sub-onboarding-print', 'ExpressSub-Sub-Onboarding-Guide', 'Sub Onboarding');
      if (success) {
        toast.success('PDF downloaded successfully!');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const step = steps[currentStep];
  const StepIcon = step.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 flex items-center justify-center p-4">
      {/* Hidden Print View for PDF - Shows all steps */}
      <div id="sub-onboarding-print" style={{ position: 'absolute', left: '-9999px', width: '800px', padding: '40px', background: 'white' }}>
        <div className="flex items-center gap-3 mb-6">
          <BrandLogo variant="black" size="xl" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Substitute Teacher Onboarding Guide</h1>
        <p className="text-slate-600 mb-8">Getting started with ExpressSub</p>
        
        {steps.map((s, index) => {
          const Icon = s.icon;
          return (
            <div key={s.id} className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Icon className="h-4 w-4 text-emerald-600" />
                </div>
                <h2 className="text-lg font-bold">{s.title}</h2>
              </div>
              <p className="text-slate-500 mb-3">{s.subtitle}</p>
              <div className="pl-11">{s.content}</div>
            </div>
          );
        })}
      </div>

      {/* Interactive View */}
      <Card className="w-full max-w-xl">
        <CardContent className="p-0">
          {/* Header */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BrandLogo variant="black" size="md" />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleDownloadPDF} disabled={isExporting} data-testid="download-sub-onboarding-pdf">
                  {isExporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                  PDF
                </Button>
                <Link to="/guides/sub">
                  <Button variant="ghost" size="sm">
                    <BookOpen className="h-4 w-4 mr-1" />
                    Full Guide
                  </Button>
                </Link>
              </div>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-slate-500 mt-2">Step {currentStep + 1} of {steps.length}</p>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <StepIcon className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">{step.title}</h2>
                <p className="text-sm text-slate-500">{step.subtitle}</p>
              </div>
            </div>
            
            <div className="mt-6">
              {step.content}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-slate-200 flex items-center justify-between">
            <div>
              {currentStep > 0 ? (
                <Button variant="ghost" onClick={handlePrev}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              ) : (
                <Button variant="ghost" onClick={handleSkip} className="text-slate-500">
                  Skip for now
                </Button>
              )}
            </div>
            <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700">
              {currentStep === steps.length - 1 ? 'Go to Dashboard' : 'Next'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
